import {
  useEffect, useMemo, useRef, useState,
} from 'react';
import { KinesisVideo } from '@aws-sdk/client-kinesis-video';
import { Role, SignalingClient } from 'amazon-kinesis-video-streams-webrtc';
import { v4 as uuid } from 'uuid';
import { useIceServers } from './useIceServers';
import { useLocalMedia } from './useLocalMedia';
import { useSignalingChannelEndpoints } from './useSignalingChannelEndpoints';
import { useSignalingClient } from './useSignalingClient';
import {
  ERROR_ICE_CANDIDATE_NOT_FOUND,
  ERROR_PEER_CONNECTION_LOCAL_DESCRIPTION_REQUIRED,
  ERROR_PEER_CONNECTION_NOT_INITIALIZED,
  ERROR_SIGNALING_CLIENT_NOT_CONNECTED,
} from './constants';
import { PeerConfigOptions, Peer } from './types';
import { logger } from '../../../API/services/SharedService';

const MAX_RETRIES = 3; // Max number of retry attempts
const RETRY_DELAY_MS = 2000; // Retry delay in ms (2 seconds)

export function useViewer(
  config: Omit<PeerConfigOptions, 'media'> & {
    media?: PeerConfigOptions['media'];
    clientId: string;
  },
): {
    signalingClient: SignalingClient | undefined;
    error: Error | undefined;
    localMedia: MediaStream | undefined;
    peer: Peer | undefined;
    peerConnection: any
  } {
  const {
    channelARN, credentials, region, media,
  } = config;

  const { error: streamError, media: localMedia } = useLocalMedia(
    media || { audio: false, video: false },
  );
  const role = Role.VIEWER;
  const clientId = useRef<string>(uuid());

  const kinesisVideoClient = useMemo(
    () => new KinesisVideo({ region, credentials }),
    [credentials?.sessionToken],
  );
  const [peerConnection, setPeerConnection] = useState<RTCPeerConnection>();
  const [peerMedia, setPeerMedia] = useState<MediaStream>();
  const [peerError, setPeerError] = useState<Error>();
  const [signalingStatus, setSignalingStatus] = useState('');
  const viewerOnly = !media;
  const localMediaIsActive = Boolean(localMedia);

  const { error: signalingChannelEndpointsError, signalingChannelEndpoints } = useSignalingChannelEndpoints({
    channelARN,
    kinesisVideoClient,
    role,
  });
  const { error: iceServersError, iceServers } = useIceServers({
    channelARN,
    channelEndpoint: signalingChannelEndpoints?.HTTPS,
    credentials,
    region,
  });

  const { error: signalingClientError, signalingClient } = useSignalingClient({
    channelARN,
    channelEndpoint: signalingChannelEndpoints?.WSS,
    clientId: clientId.current,
    credentials,
    region,
    role,
    systemClockOffset: kinesisVideoClient.config.systemClockOffset,
  });

  const depsError = signalingChannelEndpointsError || iceServersError || signalingClientError;

  const peer = {
    id: clientId.current,
    connection: peerConnection,
    media: peerMedia,
  };
  /** Initialize the peer connection with ice servers. */
  useEffect(() => {
    if (peerConnection) {
      peerConnection.close(); // Close the old connection if it exists
    }
    if (!iceServers) {
      return;
    }
    if (!viewerOnly && !localMediaIsActive) {
      return;
    }
    setPeerConnection(new RTCPeerConnection({ iceServers, iceTransportPolicy: 'all' }));
  }, [localMediaIsActive, iceServers, viewerOnly]);
  useEffect(() => () => {
    peerMedia?.getTracks()?.forEach((track) => track.stop());
  }, [peerMedia]);

  /* eslint-disable consistent-return */
  // Added retryCount as a dependency to trigger retry correctly

  /** Handle signaling client and remote peer lifecycle. */
  useEffect(() => {
    if (!peerConnection || !signalingClient) {
      return undefined;
    }
    async function handleSignalingClientOpen(): Promise<void> {
      logger.info(`[${clientId.current}] signaling client opened`);

      if (viewerOnly) {
        const videoTransceiver = peerConnection?.addTransceiver('video');
        peerConnection
          ?.getTransceivers()
          .forEach((t) => {
            t.direction = 'recvonly'; // No assignment return
          });

        // The full codec list Chrome offers (VP8/VP9 profiles/H264 profiles/AV1/H265/...)
        // pushes SDP size past the camera's signaling buffer limit (KVS SDK 30f52d1:
        // MAX_SIGNALING_MESSAGE_LEN 10240B vs MAX_SESSION_DESCRIPTION_INIT_SDP_LEN 25000B),
        // so the offer is silently dropped (0x5d000028) and no answer/ICE ever comes back.
        // The camera only ever answers with H264, so trim the offer down to that.
        if (videoTransceiver && 'setCodecPreferences' in videoTransceiver
          && typeof RTCRtpReceiver !== 'undefined'
          && typeof RTCRtpReceiver.getCapabilities === 'function') {
          const capabilities = RTCRtpReceiver.getCapabilities('video');
          const h264Codecs = capabilities?.codecs.filter(
            (codec) => codec.mimeType.toLowerCase() === 'video/h264',
          );
          if (h264Codecs?.length) {
            videoTransceiver.setCodecPreferences(h264Codecs);
          }
        }
      }
      const sessionDescription = await peerConnection?.createOffer({
        offerToReceiveAudio: true,
        offerToReceiveVideo: true,
      });
      console.log('SDP OFFER CREATED:', sessionDescription?.sdp);
      try {
        await peerConnection?.setLocalDescription(sessionDescription);
        console.log('LOCAL SDP SET:', peerConnection?.localDescription?.sdp);
      } catch (error) {
        console.error(error);
        setPeerError(error as Error);
        return;
      }

      if (!peerConnection?.localDescription) {
        setPeerError(
          new Error(ERROR_PEER_CONNECTION_LOCAL_DESCRIPTION_REQUIRED),
        );
        return;
      }

      logger.info(`[${clientId.current}] sending sdp offer`);

      signalingClient?.sendSdpOffer(peerConnection.localDescription);
    }

    async function handleSignalingClientSdpAnswer(
      answer: RTCSessionDescriptionInit,
    ): Promise<void> {
      logger.info(`[${clientId.current}] received sdp answer`);

      if (!peerConnection) {
        throw new Error(ERROR_PEER_CONNECTION_NOT_INITIALIZED);
      }
      console.log('SDP ANSWER RECEIVED:', answer?.sdp);
      await peerConnection.setRemoteDescription(answer);
    }

    function handleSignalingChannelIceCandidate(candidate: RTCIceCandidate): void {
      logger.info(
        `[${clientId.current}] received signaling channel ice candidate`,
      );

      if (!candidate) {
        throw new Error(ERROR_ICE_CANDIDATE_NOT_FOUND);
      }

      if (!peerConnection) {
        throw new Error(ERROR_PEER_CONNECTION_NOT_INITIALIZED);
      }
      console.log('RECEIVED ICE CANDIDATE:', candidate?.candidate);
      peerConnection?.addIceCandidate(candidate);
    }

    function handlePeerIceCandidate({ candidate }: RTCPeerConnectionIceEvent): void {
      logger.info(
        `[${clientId.current}] received peer ice candidate`,
      );

      if (!signalingClient) {
        throw new Error(ERROR_SIGNALING_CLIENT_NOT_CONNECTED);
      }

      if (candidate) {
        signalingClient.sendIceCandidate(candidate);
      }
    }

    function handlePeerTrack({ streams = [] }: RTCTrackEvent): void {
      logger.info(`[${clientId.current}] received peer track`);

      setPeerMedia(streams[0]);
    }
    /* eslint-disable consistent-return */
    const handleClose = (): void => {
      setSignalingStatus('closed');
    };
    /* eslint-disable consistent-return */
    const handleOpen = (): void => {
      setSignalingStatus('opened');
    };

    signalingClient.on('open', handleSignalingClientOpen);
    signalingClient.on('sdpAnswer', handleSignalingClientSdpAnswer);
    signalingClient.on('iceCandidate', handleSignalingChannelIceCandidate);
    signalingClient.addListener('open', handleOpen);
    signalingClient.addListener('close', handleClose);
    signalingClient.open();

    peerConnection.addEventListener('icecandidate', handlePeerIceCandidate);
    peerConnection.addEventListener('track', handlePeerTrack);

    return function cleanup(): void {
      logger.info(`[${clientId.current}] cleanup`);

      signalingClient.off('open', handleSignalingClientOpen);
      signalingClient.off('sdpAnswer', handleSignalingClientSdpAnswer);
      signalingClient.off('iceCandidate', handleSignalingChannelIceCandidate);
      signalingClient.close();
      peerConnection.removeEventListener(
        'icecandidate',
        handlePeerIceCandidate,
      );
      peerConnection.removeEventListener('track', handlePeerTrack);
      peerConnection.close();
    };
  }, [
    clientId,
    localMediaIsActive,
    peerConnection,
    signalingClient,
    viewerOnly,
  ]);

  /** Handle peer media lifecycle. */
  useEffect(() => () => {
    peerMedia?.getTracks()?.forEach((track) => track.stop());
  }, [peerMedia]);

  /** Send local media stream to remote peer. */
  useEffect(() => {
    if (!localMedia || !peer.connection) {
      return;
    }

    localMedia.getTracks()?.forEach((track: MediaStreamTrack) => {
      (peer.connection as RTCPeerConnection).addTrack(track, localMedia);
    });
  }, [localMedia, peer.connection]);

  return {
    signalingClient,
    error: depsError || streamError || peerError,
    localMedia,
    peer,
    peerConnection,
  };
}
